import type { ReactiveController, ReactiveControllerHost } from 'lit'

/**
 * Maps a single state value to the `data-*` attributes it should set, or `null`
 * to set none. Mirrors Base UI's per-key entries in `stateAttributesMapping`
 * (e.g. a tri-state `valid` that resolves to `data-valid` or `data-invalid`).
 */
export type StateAttributeMapper = (value: unknown) => Record<string, string> | null

export type StateAttributesMapping = Record<string, StateAttributeMapper>

export interface StateAttributesOptions {
  /**
   * Track focus-within on the host and reflect it as `data-focused`. The host
   * keeps the attribute while it or any descendant (including across a shadow
   * boundary) holds focus. Saves each component from wiring its own
   * focusin/focusout listeners.
   */
  trackFocus?: boolean
  /** Custom per-key mapping; keys not listed fall back to the boolean default. */
  mapping?: StateAttributesMapping
}

/** Default mapping for a boolean state key `foo`: present `data-foo` when truthy. */
function defaultBooleanMapper(key: string): StateAttributeMapper {
  const attribute = `data-${key}`
  return value => (value ? { [attribute]: '' } : null)
}

/**
 * Reflects a host's state onto `data-*` attributes after every update — the Lit
 * equivalent of Base UI's `stateAttributesMapping` applied by `useRenderElement`.
 * Each state key toggles `data-<key>` by default; pass `mapping` to customise
 * specific keys. With `trackFocus`, it also tracks focus-within and reflects
 * `data-focused`. The controller only ever touches the attributes it sets itself,
 * so it never clobbers consumer- or Lit-managed ones.
 *
 * @example
 * ```ts
 * // data-invalid from a property, data-focused tracked automatically
 * new StateAttributesController(this, () => ({ invalid: this.invalid }), { trackFocus: true })
 * ```
 */
export class StateAttributesController implements ReactiveController {
  private owned = new Set<string>()
  private hasFocus = false

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private getState: () => Record<string, unknown> = () => ({}),
    private options: StateAttributesOptions = {},
  ) {
    host.addController(this)
  }

  hostConnected() {
    if (this.options.trackFocus) {
      this.host.addEventListener('focusin', this.handleFocusIn)
      this.host.addEventListener('focusout', this.handleFocusOut)
    }
  }

  hostDisconnected() {
    this.host.removeEventListener('focusin', this.handleFocusIn)
    this.host.removeEventListener('focusout', this.handleFocusOut)
  }

  hostUpdated() {
    this.apply()
  }

  private handleFocusIn = () => {
    if (!this.hasFocus) {
      this.hasFocus = true
      this.host.requestUpdate()
    }
  }

  private handleFocusOut = (e: FocusEvent) => {
    // Keep focus while it moves between the host's own descendants; only clear
    // when it leaves the host entirely.
    if (this.host.contains(e.relatedTarget as Node | null)) {
      return
    }
    if (this.hasFocus) {
      this.hasFocus = false
      this.host.requestUpdate()
    }
  }

  private apply() {
    const state: Record<string, unknown> = { ...this.getState() }
    if (this.options.trackFocus) {
      state.focused = this.hasFocus
    }

    const mapping = this.options.mapping ?? {}
    const next = new Set<string>()

    for (const [key, value] of Object.entries(state)) {
      const mapper = mapping[key] ?? defaultBooleanMapper(key)
      const attributes = mapper(value)
      if (!attributes) {
        continue
      }
      for (const [attribute, attributeValue] of Object.entries(attributes)) {
        if (this.host.getAttribute(attribute) !== attributeValue) {
          this.host.setAttribute(attribute, attributeValue)
        }
        next.add(attribute)
      }
    }

    // Drop attributes we set on a previous run that no longer apply.
    for (const attribute of this.owned) {
      if (!next.has(attribute)) {
        this.host.removeAttribute(attribute)
      }
    }
    this.owned = next
  }
}
